<template>
  <!-- Planeerija in the hooldusplaat look (Seaded -> Planeerija uues
       kujunduses), agreed on a mockup first: one month with its weekdays and
       holidays, brushes with a code and the month's count, cells that say
       their code as well as their colour, who is on valve each day, the
       töökäsud in the same grid, and saving you can see and undo. -->
  <div class="pp">
    <div class="pp-head">
      <div class="pp-head__text">
        <div class="pp-cap">Planeerija</div>
        <h1 class="pp-title">{{ monthTitle }}</h1>
      </div>
      <div class="pp-nav">
        <button type="button" class="pp-icon" aria-label="Eelmine kuu" @click="go(-1)"><v-icon>mdi-chevron-left</v-icon></button>
        <button type="button" class="pp-out" @click="goToday()">Täna</button>
        <button type="button" class="pp-icon" aria-label="Järgmine kuu" @click="go(1)"><v-icon>mdi-chevron-right</v-icon></button>
        <!-- How many months at once; each person's own (Minu seaded keeps it). -->
        <div class="pp-seg" role="group" aria-label="Mitu kuud korraga">
          <button
            v-for="n in [1, 2, 3]"
            :key="n"
            type="button"
            :aria-pressed="String(span === n)"
            @click="setSpan(n)"
          >{{ n }} {{ n === 1 ? 'kuu' : 'kuud' }}</button>
        </div>
        <button type="button" class="pp-out pp-out--quiet" title="Sinu valik, teisi ei muuda. Tagasi saab vana lehe nupust „Uus vaade“." @click="$emit('use-old')">Vana vaade</button>
      </div>
    </div>

    <!-- A technician reads the planner: the same bar is then the key to the
         codes, with the month's counts, and nothing in it can be pressed. -->
    <div v-if="readonly" class="pp-brushes" aria-label="Tähised">
      <span class="pp-k">TÄHISED</span>
      <span v-for="type in brushes" :key="type.id" class="pp-brush pp-brush--key">
        <span class="pp-code" :style="{ background: fill(type) }">{{ type.code }}</span>{{ type.name }}<small v-if="counts[type.id]">{{ counts[type.id] }}</small>
      </span>
      <div class="pp-brushes__end">Ainult vaatamiseks</div>
    </div>
    <div v-else class="pp-brushes" role="group" aria-label="Pintsel">
      <span class="pp-k">PINTSEL</span>
      <button
        v-for="type in brushes"
        :key="type.id"
        type="button"
        class="pp-brush"
        :aria-pressed="String(brush === type.id)"
        @click="brush = type.id"
      >
        <span class="pp-code" :style="{ background: fill(type) }">{{ type.code }}</span>{{ type.name }}<small v-if="counts[type.id]">{{ counts[type.id] }}</small>
      </button>
      <button type="button" class="pp-brush pp-brush--erase" :aria-pressed="String(brush === ERASE)" @click="brush = ERASE">
        <span class="pp-code">✕</span>Tühjenda
      </button>
      <div class="pp-brushes__end">
        <span class="pp-saved" :class="{ busy: saving, bad: saveError }"><i />{{ savedText }}</span>
        <button type="button" class="pp-link" :disabled="!undoStack.length || saving" @click="undo()">↶ Võta tagasi</button>
      </div>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="green" height="3" />
    <div v-for="(m, mi) in months" :key="m.key" class="pp-card">
      <div v-if="months.length > 1" class="pp-month">{{ m.title }}</div>
      <div class="pp-scroll">
        <table class="pp-grid" :class="{ 'pp-grid--read': readonly }" @pointerdown="down" @pointerover="over">
          <thead>
            <tr>
              <th class="pp-who">TEHNIK</th>
              <th
                v-for="day in m.days"
                :key="day.date"
                :class="{ we: day.isWeekend, hol: day.holiday, today: day.date === today }"
                :title="day.holiday || null"
              >{{ WD[day.wd] }}<b>{{ day.day }}</b></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="technic in technics" :key="technic.id">
              <th class="pp-who">
                <span class="pp-person">
                  <span class="pp-ini" :style="dot(technic.color)">{{ initials(technic.fullName) }}</span>
                  <span><b>{{ technic.fullName }}</b><span>{{ summary(technic, m.key) }}</span></span>
                </span>
              </th>
              <td
                v-for="day in m.days"
                :key="day.date"
                class="pp-day"
                :class="{ we: day.isWeekend, hol: day.holiday, clash: clash(technic, day), today: day.date === today }"
                :style="cellStyle(technic, day)"
                :data-t="technic.id"
                :data-d="day.date"
                :title="cellTitle(technic, day)"
              >{{ cellCode(technic, day) }}<span v-if="workOn(technic, day)" class="pp-n">{{ workOn(technic, day) }}</span></td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th class="pp-who">VALVES</th>
              <td
                v-for="day in m.days"
                :key="day.date"
                :class="{ gap: !onValve(day).length && !day.isWeekend && !day.holiday }"
                :title="onValve(day).map(t => t.fullName).join(', ') || 'Keegi pole valves'"
              >
                <span v-if="onValve(day).length" class="pp-mini" :style="dot(onValve(day)[0].color)">{{ initials(onValve(day)[0].fullName) }}</span><template v-if="onValve(day).length > 1">+{{ onValve(day).length - 1 }}</template>
                <template v-else-if="!onValve(day).length && !day.isWeekend && !day.holiday">!</template>
              </td>
            </tr>
            <tr>
              <th class="pp-who">EEMAL (V, P)</th>
              <td v-for="day in m.days" :key="day.date" :class="{ thin: away(day) >= 3 && !day.isWeekend }">{{ away(day) || '' }}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div v-if="mi === months.length - 1" class="pp-legend">
        <span><i class="pp-l-we" />Nädalavahetus</span>
        <span><i class="pp-l-hol" />Riigipüha</span>
        <span><i class="pp-l-n" />Töökäske sel päeval</span>
        <span><i class="pp-l-clash" />Töökäsk vabal päeval / keegi pole valves</span>
        <span>Tühi lahter: tavaline tööpäev</span>
        <span v-if="!readonly">Telefonis: puuduta lahtrit</span>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api'
import dayjs from 'dayjs'
import loadArchivo from '@/plugins/archivo'
import { initials } from '@/components/frame/frameLinks'
import { capitalize } from '@/plugins/utils'

// A short code for each workday type, by its stored title; a type added later
// gets the first two letters of its name.
const CODES = { vaba: 'V', office: 'K', valve: 'VA', puhkus: 'P', lyhike: 'L', lepingulised: 'LE', kontorivalve: 'KV' }
// Brushes in this order; the rest after them.
const ORDER = ['valve', 'office', 'kontorivalve', 'lepingulised', 'lyhike', 'vaba', 'puhkus']
const VALVE = ['valve', 'kontorivalve']
const AWAY = ['vaba', 'puhkus']
// The 'tyhi' workday is how the old page erases; here it is the Tühjenda brush.
const ERASE = 'erase'
const WD = ['P', 'E', 'T', 'K', 'N', 'R', 'L']

export default {
  emits: ['use-old'],
  props: {
    // A technician's planner: nothing is painted or sent.
    readonly: { type: Boolean, default: false }
  },
  data () {
    return {
      ERASE,
      WD,
      loading: false,
      month: dayjs(this.$route.params.date || undefined).startOf('month'),
      // The months on screen: { key: 'YYYY-MM', title, days }.
      months: [],
      technics: [],
      workdays: [],
      // plan[technicId][date] = workday id
      plan: {},
      // work[technicId][date] = töökäske
      work: {},
      brush: null,
      painting: null,
      undoStack: [],
      saving: false,
      saveError: false,
      savedAt: null,
      today: dayjs().format('YYYY-MM-DD')
    }
  },
  computed: {
    // 1, 2 or 3 months, from Minu seaded (userPrefs.plannerMonths).
    span () {
      const n = Number(this.$store.state.prefs && this.$store.state.prefs.plannerMonths)
      return [1, 2, 3].includes(n) ? n : 1
    },
    monthTitle () {
      if (this.span === 1) return capitalize(this.month.format('MMMM YYYY'))
      const last = this.month.add(this.span - 1, 'month')
      return capitalize(this.month.format(this.month.isSame(last, 'year') ? 'MMMM' : 'MMMM YYYY')) + ' – ' + last.format('MMMM YYYY')
    },
    types () {
      return this.workdays.filter(w => w.title !== 'tyhi').map(w => ({
        ...w,
        code: CODES[w.title] || (w.name || w.title).slice(0, 2).toUpperCase(),
        hex: '#' + String(w.color || 'cccccc').replace('#', '')
      }))
    },
    brushes () {
      const rank = t => (ORDER.indexOf(t.title) + 1) || 99
      return this.types.slice().sort((a, b) => rank(a) - rank(b))
    },
    counts () {
      const counts = {}
      for (const row of Object.values(this.plan)) {
        for (const id of Object.values(row)) counts[id] = (counts[id] || 0) + 1
      }
      return counts
    },
    savedText () {
      if (this.saving) return 'Salvestan…'
      if (this.saveError) return 'Salvestamine ebaõnnestus'
      return this.savedAt ? 'Salvestatud ' + this.savedAt : 'Kõik salvestatud'
    }
  },
  watch: {
    span () {
      this.refresh()
    },
    '$route.params.date' (date) {
      const month = dayjs(date || undefined).startOf('month')
      if (!month.isSame(this.month, 'month')) {
        this.month = month
        this.refresh()
      }
    }
  },
  async created () {
    loadArchivo()
    this.loading = true
    try {
      const [{ users }, { workdays }] = await Promise.all([api.getPlannerUsers(), api.getWorkdays()])
      this.technics = users
      this.workdays = workdays
      const first = this.brushes[0]
      this.brush = first ? first.id : ERASE
    } catch (err) {
      this.$store.dispatch('showNotification', { text: err.message, color: 'red' })
    }
    await this.refresh()
    window.addEventListener('pointerup', this.up)
  },
  beforeUnmount () {
    window.removeEventListener('pointerup', this.up)
  },
  methods: {
    initials,
    async refresh () {
      this.loading = true
      const span = this.span
      const start = this.month.startOf('month')
      const end = this.month.add(span - 1, 'month').endOf('month')
      const asked = start.format('YYYY-MM') + '/' + span
      const firsts = Array.from({ length: span }, (_, i) => start.add(i, 'month'))
      try {
        const [months, planner, work] = await Promise.all([
          Promise.all(firsts.map(first => api.getPlannerMonth({ date: first.toISOString() }))),
          api.getPlanner({ startDate: start.toISOString(), endDate: end.toISOString() }),
          api.getPlannerWork({ startDate: start.format('YYYY-MM-DD'), endDate: end.format('YYYY-MM-DD') })
        ])
        if (asked !== this.month.format('YYYY-MM') + '/' + this.span) return
        this.months = months.map((month, i) => ({
          key: firsts[i].format('YYYY-MM'),
          title: capitalize(firsts[i].format('MMMM YYYY')),
          days: month.days.map(day => ({ ...day, wd: dayjs(day.date).day() }))
        }))
        const plan = {}
        for (const technic of this.technics) plan[technic.id] = {}
        for (const event of planner.events) {
          if (!plan[event.user.id] || !event.workday) continue
          plan[event.user.id][dayjs(event.startDate).format('YYYY-MM-DD')] = event.workday.id
        }
        this.plan = plan
        this.work = work.work || {}
        this.undoStack = []
      } catch (err) {
        this.$store.dispatch('showNotification', { text: err.message, color: 'red' })
      } finally {
        this.loading = false
      }
    },
    setSpan (n) {
      if (n !== this.span) this.$store.dispatch('savePrefs', { plannerMonths: String(n) })
    },
    go (step) {
      this.toMonth(this.month.add(step, 'month'))
    },
    goToday () {
      this.toMonth(dayjs().startOf('month'))
    },
    toMonth (month) {
      this.$router.push({ name: 'planner', params: { date: month.format('YYYY-MM') } }).catch(() => {})
    },
    typeOf (technic, day) {
      const id = (this.plan[technic.id] || {})[day.date]
      return id ? this.types.find(t => t.id === id) : null
    },
    workOn (technic, day) {
      return (this.work[technic.id] || {})[day.date] || 0
    },
    clash (technic, day) {
      const type = this.typeOf(technic, day)
      return !!(type && AWAY.includes(type.title) && this.workOn(technic, day))
    },
    onValve (day) {
      return this.technics.filter(technic => {
        const type = this.typeOf(technic, day)
        return type && VALVE.includes(type.title)
      })
    },
    away (day) {
      return this.technics.filter(technic => {
        const type = this.typeOf(technic, day)
        return type && AWAY.includes(type.title)
      }).length
    },
    // That month's days only, so each month's row says its own.
    summary (technic, key) {
      const counts = {}
      for (const [date, id] of Object.entries(this.plan[technic.id] || {})) {
        if (!key || date.startsWith(key)) counts[id] = (counts[id] || 0) + 1
      }
      const text = this.types.filter(t => counts[t.id]).sort((a, b) => counts[b.id] - counts[a.id]).slice(0, 3)
        .map(t => t.name + ' ' + counts[t.id]).join(' · ')
      return text || 'Kõik päevad tavalised'
    },
    // The stored colours, softer, so the code reads on them.
    fill (type) {
      const n = parseInt(type.hex.slice(1, 7), 16)
      if (isNaN(n)) return '#E9EEEC'
      const a = type.title === 'office' ? 0.55 : 0.42
      return 'rgb(' + [n >> 16 & 255, n >> 8 & 255, n & 255].map(v => Math.round(v * a + 255 * (1 - a))).join(',') + ')'
    },
    cellStyle (technic, day) {
      const type = this.typeOf(technic, day)
      return type ? { background: this.fill(type) } : null
    },
    cellCode (technic, day) {
      const type = this.typeOf(technic, day)
      return type ? type.code : ''
    },
    cellTitle (technic, day) {
      const type = this.typeOf(technic, day)
      const n = this.workOn(technic, day)
      return technic.fullName + ', ' + dayjs(day.date).format('DD.MM') + ': ' + (type ? type.name : 'tavaline päev') +
        (n ? ' · ' + n + (n === 1 ? ' töökäsk' : ' töökäsku') : '') +
        (this.clash(technic, day) ? ' — töökäsk vabal päeval!' : '')
    },
    dot (color) {
      const hex = String(color || '').replace('#', '')
      if (!/^[0-9a-f]{6}$/i.test(hex)) return { background: '#5B6B66', color: '#fff' }
      const [r, g, b] = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16))
      const light = (0.299 * r + 0.587 * g + 0.114 * b) > 150
      return { background: '#' + hex, color: light ? '#16322A' : '#fff' }
    },
    // A mouse paints while held across days; a finger paints the one cell it
    // taps, and a drag scrolls the month as it always does on a phone.
    down (event) {
      const cell = event.target.closest && event.target.closest('td.pp-day')
      if (!cell || !this.brush || this.readonly) return
      if (event.pointerType === 'mouse') event.preventDefault()
      this.painting = { changes: [], mouse: event.pointerType === 'mouse' }
      this.paint(cell)
    },
    over (event) {
      if (!this.painting || !this.painting.mouse) return
      const cell = event.target.closest && event.target.closest('td.pp-day')
      if (cell) this.paint(cell)
    },
    up () {
      const stroke = this.painting
      this.painting = null
      if (!stroke || !stroke.changes.length) return
      this.undoStack.push(stroke.changes)
      this.send(stroke.changes.map(change => ({ technicId: change.technicId, date: change.date, workdayId: change.after })))
    },
    paint (cell) {
      const technicId = Number(cell.dataset.t)
      const date = cell.dataset.d
      const row = this.plan[technicId] || (this.plan[technicId] = {})
      const before = row[date] || null
      const after = this.brush === ERASE ? null : this.brush
      if (before === after) return
      if (this.painting.changes.some(change => change.technicId === technicId && change.date === date)) return
      this.painting.changes.push({ technicId, date, before, after })
      if (after) row[date] = after
      else delete row[date]
    },
    undo () {
      if (this.readonly) return
      const changes = this.undoStack.pop()
      if (!changes) return
      for (const change of changes) {
        const row = this.plan[change.technicId] || (this.plan[change.technicId] = {})
        if (change.before) row[change.date] = change.before
        else delete row[change.date]
      }
      this.send(changes.map(change => ({ technicId: change.technicId, date: change.date, workdayId: change.before })))
    },
    // One request per stroke; a failure is said and the month read again, so
    // the grid never shows what the server did not keep.
    async send (updates) {
      this.saving = true
      this.saveError = false
      try {
        await api.addToPlanner(updates.map(update => ({ ...update, workdayId: update.workdayId || false })))
        this.savedAt = dayjs().format('HH:mm')
      } catch (err) {
        this.saveError = true
        this.$store.dispatch('showNotification', { text: err.message || 'Salvestamine ebaõnnestus', color: 'red' })
        await this.refresh()
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.pp {
  --p-surface: #FFFFFF; --p-soft: #FAFBFA; --p-ink: #16322A; --p-ink-2: #3E504A; --p-ink-3: #5B6B66;
  --p-line: #C9D2CE; --p-line-soft: #E1E7E4; --p-green: #2E7D32; --p-blue: #1F5FD1; --p-blue-bg: #EEF3FC;
  --p-amber: #8F5400; --p-amber-bg: #FFF4E0; --p-amber-ink: #5B3700; --p-red: #B3261E; --p-red-bg: #FCEDEC; --p-weekend: #EEF1EF;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif; color: var(--p-ink); font-variant-numeric: tabular-nums;
  display: flex; flex-direction: column; gap: 14px; padding-bottom: 40px;
}
.pp button { font: inherit; color: inherit; }
.pp :focus-visible { outline: 3px solid var(--p-blue); outline-offset: 2px; }
.pp-head { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
.pp-head__text { flex: 1 1 260px; min-width: 0; }
.pp-cap { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: var(--p-ink-3); font-weight: 600; }
.pp-title { font-size: 30px; font-weight: 700; margin: 2px 0 0; line-height: 1.15; font-stretch: 125%; }
.pp-nav { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.pp-icon { width: 38px; height: 38px; border-radius: 6px; border: 1.5px solid var(--p-line); background: var(--p-surface); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.pp-out { height: 38px; padding: 0 14px; border-radius: 6px; border: 1.5px solid var(--p-line); background: var(--p-surface); font-weight: 600; font-size: 14px; cursor: pointer; }
.pp-out--quiet { border-color: transparent; background: none; color: var(--p-ink-3); text-decoration: underline; text-underline-offset: 3px; margin-left: 6px; }

.pp-brushes { position: sticky; top: calc(var(--v-layout-top, 0px) + 8px); z-index: 4; background: var(--p-surface); border: 1px solid var(--p-line); border-radius: 12px; padding: 10px 12px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; box-shadow: 0 6px 18px rgba(22, 50, 42, .06); }
.pp-k { font-size: 11px; font-weight: 700; letter-spacing: .07em; color: var(--p-ink-3); margin-right: 4px; }
.pp-brush { display: inline-flex; align-items: center; gap: 8px; height: 34px; padding: 0 12px 0 6px; border-radius: 17px; border: 1.5px solid var(--p-line); background: var(--p-surface); cursor: pointer; font-size: 13.5px; font-weight: 600; color: var(--p-ink-2); }
.pp-brush[aria-pressed="true"] { border-color: var(--p-ink); box-shadow: inset 0 0 0 1px var(--p-ink); color: var(--p-ink); }
.pp-brush small { font-weight: 700; color: var(--p-ink-3); }
.pp-brush--key { cursor: default; border-color: var(--p-line-soft); }
.pp-code { width: 24px; height: 24px; border-radius: 12px; display: inline-flex; align-items: center; justify-content: center; font-size: 10.5px; font-weight: 800; color: #16322A; }
.pp-brush--erase .pp-code { background: var(--p-surface); box-shadow: inset 0 0 0 1.5px var(--p-line); color: var(--p-ink-3); }
.pp-brushes__end { margin-left: auto; display: flex; align-items: center; gap: 12px; font-size: 12.5px; color: var(--p-ink-3); }
.pp-saved { display: inline-flex; align-items: center; gap: 6px; }
.pp-saved i { width: 8px; height: 8px; border-radius: 4px; background: var(--p-green); display: inline-block; }
.pp-saved.busy i { background: var(--p-amber); }
.pp-saved.bad { color: var(--p-red); }
.pp-saved.bad i { background: var(--p-red); }
.pp-link { border: 0; background: none; padding: 0; color: var(--p-green); font-weight: 600; font-size: 13px; cursor: pointer; }
.pp-link:disabled { color: var(--p-ink-3); cursor: default; }

.pp-card { background: var(--p-surface); border: 1px solid var(--p-line); border-radius: 12px; overflow: hidden; }
.pp-month { padding: 10px 14px; font-size: 16px; font-weight: 700; font-stretch: 112%; border-bottom: 1px solid var(--p-line); }
.pp-seg { display: inline-flex; border: 1.5px solid var(--p-line); border-radius: 8px; overflow: hidden; background: var(--p-surface); margin-left: 4px; }
.pp-seg button { border: 0; background: none; height: 35px; padding: 0 12px; font-size: 13.5px; font-weight: 600; color: var(--p-ink-2); cursor: pointer; white-space: nowrap; }
.pp-seg button + button { border-left: 1.5px solid var(--p-line); }
.pp-seg button[aria-pressed="true"] { background: var(--p-ink); color: #fff; }
.pp-scroll { overflow-x: auto; }
.pp-grid { border-collapse: separate; border-spacing: 0; width: 100%; min-width: 900px; user-select: none; }
.pp-grid th, .pp-grid td { padding: 0; text-align: center; }
.pp-grid thead th { font-size: 11px; color: var(--p-ink-3); font-weight: 600; padding: 8px 0 6px; border-bottom: 1px solid var(--p-line); }
.pp-grid thead th b { display: block; font-size: 13px; color: var(--p-ink); font-weight: 700; }
.pp-grid thead th.we { background: var(--p-weekend); }
.pp-grid thead th.hol { background: var(--p-red-bg); color: var(--p-red); }
.pp-grid thead th.hol b { color: var(--p-red); }
.pp-grid thead th.today b { display: inline-block; background: var(--p-ink); color: #fff; border-radius: 10px; padding: 0 5px; }
.pp-who { position: sticky; left: 0; z-index: 2; background: var(--p-surface); text-align: left !important; padding: 8px 14px !important; min-width: 168px; border-right: 1px solid var(--p-line); border-bottom: 1px solid var(--p-line-soft); }
.pp-grid thead .pp-who { font-size: 11px; font-weight: 700; letter-spacing: .07em; vertical-align: bottom; z-index: 3; }
.pp-person { display: flex; align-items: center; gap: 10px; }
.pp-ini { width: 30px; height: 30px; border-radius: 15px; display: inline-flex; align-items: center; justify-content: center; font-size: 11.5px; font-weight: 700; flex: none; }
.pp-person b { display: block; font-size: 14px; white-space: nowrap; }
.pp-person span span { display: block; font-size: 11.5px; color: var(--p-ink-3); font-weight: 400; white-space: nowrap; }
.pp-day { height: 44px; min-width: 24px; border-bottom: 1px solid var(--p-line-soft); border-right: 1px solid var(--p-line-soft); cursor: crosshair; position: relative; font-size: 10.5px; font-weight: 800; color: #16322A; }
.pp-day.we { background: var(--p-weekend); }
.pp-day.hol { background: var(--p-red-bg); }
.pp-day:hover { box-shadow: inset 0 0 0 2px var(--p-ink-3); }
.pp-grid--read { user-select: auto; }
.pp-grid--read .pp-day { cursor: default; }
.pp-grid--read .pp-day:not(.clash):hover { box-shadow: none; }
.pp-day.clash { box-shadow: inset 0 0 0 2px var(--p-amber); }
.pp-n { position: absolute; right: 2px; bottom: 2px; min-width: 13px; height: 13px; border-radius: 7px; background: var(--p-blue); color: #fff; font-size: 9px; line-height: 13px; font-weight: 700; padding: 0 3px; }
.pp-grid tfoot .pp-who { font-size: 11px; font-weight: 700; letter-spacing: .07em; color: var(--p-ink-3); background: var(--p-soft); }
.pp-grid tfoot td { height: 34px; background: var(--p-soft); border-bottom: 1px solid var(--p-line-soft); border-right: 1px solid var(--p-line-soft); font-size: 10px; font-weight: 700; line-height: 1.1; }
.pp-grid tfoot td.gap { background: var(--p-amber-bg); color: var(--p-amber-ink); box-shadow: inset 0 0 0 1px var(--p-amber); }
.pp-grid tfoot td.thin { color: var(--p-amber); }
.pp-mini { width: 20px; height: 20px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 800; }
.pp-legend { display: flex; flex-wrap: wrap; gap: 6px 18px; font-size: 12.5px; color: var(--p-ink-2); padding: 10px 14px; border-top: 1px solid var(--p-line-soft); }
.pp-legend span { display: inline-flex; align-items: center; gap: 6px; }
.pp-legend i { width: 14px; height: 14px; border-radius: 3px; display: inline-block; }
.pp-l-we { background: var(--p-weekend); box-shadow: inset 0 0 0 1px var(--p-line); }
.pp-l-hol { background: var(--p-red-bg); box-shadow: inset 0 0 0 1px var(--p-red); }
.pp-l-n { background: var(--p-blue); }
.pp-l-clash { background: var(--p-amber-bg); box-shadow: inset 0 0 0 2px var(--p-amber); }

@media (max-width: 700px) {
  .pp-title { font-size: 24px; }
  .pp-brushes { position: static; }
  .pp-brushes__end { margin-left: 0; width: 100%; }
  .pp-who { min-width: 130px; padding: 6px 8px !important; }
  .pp-person span span { display: none; }
}
</style>
